import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext(null);

const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';
const DEV_SESSION = DEV_BYPASS ? {
  access_token: 'dev-bypass-token',
  user: { id: 'dev-local-user-001', email: 'dev@localhost' },
} : null;

export function AuthProvider({ children }) {
  const [session, setSession] = useState(DEV_BYPASS ? DEV_SESSION : undefined); // undefined = loading
  const [kitchens, setKitchens] = useState([]);
  const [activeKitchen, setActiveKitchen] = useState(null);
  const [invites, setInvites] = useState([]);
  const [notAllowed, setNotAllowed] = useState(false);

  // Keep token in a ref so authHeaders/apiFetch stay stable across token refreshes
  // Updated synchronously during render so child effects always see current values
  const tokenRef = useRef(DEV_BYPASS ? 'dev-bypass-token' : null);
  const kitchenIdRef = useRef('');
  tokenRef.current = session?.access_token || null;
  kitchenIdRef.current = activeKitchen?.id || '';

  useEffect(() => {
    if (DEV_BYPASS) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(prev => {
        // Only update state if auth status actually changed (different user or sign-in/out)
        // Token refreshes for the same user just update the ref
        if (prev?.user?.id === session?.user?.id) {
          tokenRef.current = session?.access_token || null;
          return prev;
        }
        return session;
      });
    });
    return () => subscription.unsubscribe();
  }, []);

  const authHeaders = useCallback(() => {
    if (!tokenRef.current) return {};
    return {
      Authorization: `Bearer ${tokenRef.current}`,
      'X-Kitchen-Id': kitchenIdRef.current,
    };
  }, []);

  const apiFetch = useCallback(async (url, options = {}) => {
    const headers = { ...authHeaders(), ...options.headers };
    const res = await fetch(url, { ...options, headers });
    return res;
  }, [authHeaders]);

  const loadUserData = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      const res = await apiFetch('/api/me');
      if (res.status === 403) {
        setNotAllowed(true);
        return;
      }
      if (!res.ok) return;
      setNotAllowed(false);
      const data = await res.json();
      setKitchens(data.kitchens || []);
      if (data.kitchens && data.kitchens.length > 0) {
        setActiveKitchen(prev => {
          const found = data.kitchens.find(k => k.id === prev?.id);
          if (!found) return data.kitchens[0];
          if (found.name === prev.name && found.role === prev.role) return prev;
          return found;
        });
      }
    } catch {}
  }, [apiFetch]);

  const loadInvites = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      const res = await apiFetch('/api/invites');
      if (res.ok) {
        setInvites(await res.json());
      }
    } catch {}
  }, [apiFetch]);

  useEffect(() => {
    if (session) {
      loadUserData();
      loadInvites();
    } else {
      setKitchens([]);
      setActiveKitchen(null);
      setInvites([]);
      setNotAllowed(false);
    }
  }, [session, loadUserData, loadInvites]);

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) console.error('Sign-in error:', error);
  };

  const signInWithEmail = async (email, password, captchaToken) => {
    const options = captchaToken ? { captchaToken } : {};
    const { error } = await supabase.auth.signInWithPassword({ email, password, options });
    return { error };
  };

  const signUp = async (email, password, captchaToken) => {
    const options = { emailRedirectTo: window.location.origin };
    if (captchaToken) {
      options.captchaToken = captchaToken;
    }
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options,
    });
    return { data, error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
  };

  const renameKitchen = async (kitchenId, name) => {
    const res = await apiFetch(`/api/kitchens/${kitchenId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    if (res.ok) {
      setKitchens(prev => prev.map(k => k.id === kitchenId ? { ...k, name } : k));
      setActiveKitchen(prev => prev && prev.id === kitchenId ? { ...prev, name } : prev);
    }
    return res.ok;
  };

  const inviteToKitchen = async (email) => {
    if (!activeKitchen) return;
    const res = await apiFetch(`/api/kitchens/${activeKitchen.id}/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return res.ok;
  };

  const acceptInvite = async (inviteId) => {
    const res = await apiFetch(`/api/invites/${inviteId}/accept`, { method: 'POST' });
    if (res.ok) {
      await loadUserData();
      await loadInvites();
    }
  };

  const value = {
    session,
    user: session?.user || null,
    kitchens,
    activeKitchen,
    setActiveKitchen,
    invites,
    notAllowed,
    signInWithGoogle,
    signInWithEmail,
    signUp,
    signOut,
    renameKitchen,
    inviteToKitchen,
    acceptInvite,
    apiFetch,
    loading: session === undefined,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
